import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { crmActivityHeartbeat, crmActivityLogin, crmActivityLogout, crmGetDueDeliveries } from "../../../../api/crmApi";
import getSignalRService from "../../../../services/signalRService";
import { dispatchNotificationsRefresh } from "../../../../types/notification.types";
import GetLocalStorageUserId from "../../../sharedComponents/GetLocalStorageuserId";
import { bumpWhatsAppInboxUnread } from "./whatsAppInboxBadge";

const CrmActivityTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (!GetLocalStorageUserId()) return undefined;
    crmActivityLogin(location.pathname).catch(() => {});
    const tick = () => crmActivityHeartbeat(window.location.pathname).catch(() => {});
    const timer = setInterval(tick, 60000);
    window.addEventListener("beforeunload", crmActivityLogout);
    return () => {
      clearInterval(timer);
      window.removeEventListener("beforeunload", crmActivityLogout);
    };
  }, []);

  useEffect(() => {
    if (!GetLocalStorageUserId()) return;
    crmActivityHeartbeat(location.pathname).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const service = getSignalRService();
    const onMention = (payload) => {
      const from = payload?.from || payload?.From || "A teammate";
      const body = payload?.body || payload?.Body || "";
      toast.info(`${from} tagged you in CRM${body ? `: ${body}` : ""}`, {
        position: "top-right",
        autoClose: 8000,
      });
      dispatchNotificationsRefresh();
    };
    service.on("CrmMention", onMention);
    const onTask = (payload) => {
      toast.info(payload?.subject || payload?.Subject || "CRM task update", {
        position: "top-right",
        autoClose: 8000,
      });
      dispatchNotificationsRefresh();
    };
    const onDelivery = (payload) => {
      const invoice = payload?.invoiceNo || payload?.InvoiceNo || payload?.orderId;
      toast.warn(`Delivery due today: ${invoice || "an order"}`, {
        position: "top-right",
        autoClose: 12000,
      });
      dispatchNotificationsRefresh();
    };
    const onWhatsApp = (payload) => {
      const direction = String(payload?.direction || payload?.Direction || "in").toLowerCase();
      const onInbox = window.location.pathname.includes("/crm/inbox");
      // Only bump the nav badge for customer inbound while staff are elsewhere.
      if (!onInbox && direction !== "out") bumpWhatsAppInboxUnread(1);
      dispatchNotificationsRefresh();
      window.dispatchEvent(
        new CustomEvent("crm-whatsapp-inbound", { detail: payload }),
      );
    };
    service.on("CrmTaskUpdate", onTask);
    service.on("CrmDeliveryDue", onDelivery);
    service.on("WhatsAppInbound", onWhatsApp);
    service.on("WhatsAppEscalation", onWhatsApp);
    return () => {
      service.off("CrmMention", onMention);
      service.off("CrmTaskUpdate", onTask);
      service.off("CrmDeliveryDue", onDelivery);
      service.off("WhatsAppInbound", onWhatsApp);
      service.off("WhatsAppEscalation", onWhatsApp);
    };
  }, []);

  useEffect(() => {
    if (!GetLocalStorageUserId()) return undefined;
    const key = `crmDueToast-${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key)) return undefined;
    crmGetDueDeliveries()
      .then((rows) => {
        if (!rows.length) return;
        sessionStorage.setItem(key, "1");
        toast.warn(
          `${rows.length} order${rows.length === 1 ? "" : "s"} due for delivery today. Check CRM Orders.`,
          { position: "top-right", autoClose: 10000 },
        );
      })
      .catch(() => {});
    return undefined;
  }, []);

  return null;
};

export default CrmActivityTracker;
